import React, { useState } from 'react';
import { Alert, Button, Input, Typography } from 'antd';
import { LockOutlined, MailOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { requestGlp1BridgeAccessCode, verifyGlp1BridgeAccessCode } from '../api/glp1BridgeChat';

const { Text } = Typography;

function normalizeEmail(value) {
  return String(value || '').trim().toLowerCase();
}

export default function Glp1BridgeProviderAccessGate({ onVerified }) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('email');
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);
  const [maskedEmail, setMaskedEmail] = useState('');

  const requestedCode = step === 'code';

  const handleRequestCode = async () => {
    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail) {
      setError('Enter your professional work email.');
      return;
    }
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      const result = await requestGlp1BridgeAccessCode({ email: normalizedEmail });
      if (result?.authenticated) {
        onVerified?.(result);
        return;
      }
      setEmail(normalizedEmail);
      setMaskedEmail(result.email || normalizedEmail);
      setStep('code');
      setNotice(`Access code sent to ${result.email || normalizedEmail}.`);
    } catch (requestError) {
      setError(requestError.message || 'Unable to send an access code right now.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    const normalizedEmail = normalizeEmail(email);
    const normalizedCode = String(code || '').replace(/\D/g, '');
    if (normalizedCode.length !== 6) {
      setError('Enter the 6-digit access code.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await verifyGlp1BridgeAccessCode({
        email: normalizedEmail,
        code: normalizedCode,
      });
      onVerified?.(result);
    } catch (verifyError) {
      setError(verifyError.message || 'Unable to verify the access code right now.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className={`glp1-access-panel${requestedCode ? ' glp1-access-panel--code' : ''}`}
      aria-labelledby="glp1-provider-access-title"
    >
      <div className="glp1-access-content">
        <div className="glp1-access-heading">
          <SafetyCertificateOutlined className="glp1-access-heading-icon" />
          <div>
            <h2 id="glp1-provider-access-title" className="glp1-access-title">
              Provider access
            </h2>
            <p className="glp1-access-copy">
              These resources are intended only for providers, clinical teams, and staff. Use your
              professional email to access them.
            </p>
            <p className="glp1-access-once-note">
              First-time setup only. Future visits from this browser will open these resources automatically.
            </p>
          </div>
        </div>

        <div className="glp1-access-feedback">
          {error ? <Alert type="error" showIcon message={error} /> : null}
          {notice ? <Alert type="success" showIcon message={notice} /> : null}
        </div>

        <div className={`glp1-access-form${requestedCode ? ' glp1-access-form--code' : ''}`}>
          <Text strong>Professional email</Text>
          <Input
            size="large"
            prefix={<MailOutlined />}
            value={email}
            disabled={requestedCode || loading}
            placeholder="name@clinic.org"
            autoComplete="email"
            onChange={(event) => setEmail(event.target.value)}
            onPressEnter={handleRequestCode}
          />

          {requestedCode ? (
            <>
              <Text strong>Access code</Text>
              <Input
                size="large"
                prefix={<LockOutlined />}
                value={code}
                maxLength={6}
                inputMode="numeric"
                placeholder="6-digit code"
                autoComplete="one-time-code"
                onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                onPressEnter={handleVerifyCode}
              />
              <Button type="link" size="small" style={{ justifySelf: 'start', padding: 0 }} disabled={loading} onClick={() => {
                setStep('email');
                setCode('');
                setNotice(null);
                setError(null);
              }}>
                Use a different email
              </Button>
            </>
          ) : null}

          <Button
            type="primary"
            size="large"
            block
            loading={loading}
            onClick={requestedCode ? handleVerifyCode : handleRequestCode}
          >
            {requestedCode ? `Verify ${maskedEmail || 'email'}` : 'Send access code'}
          </Button>
        </div>

        <p className="glp1-access-footnote">
          Personal email domains are blocked. Access is for healthcare providers and clinical or coverage teams.
        </p>
      </div>
    </section>
  );
}
